/**
 * Error codes and curator-facing copy for the PDF analysis API.
 *
 * Mirrors `CatalogImportError` in nine-generics (16201-16299):
 *  - 16201-16249 are REQUEST-level: the API answers with them and a real HTTP
 *    status (a NineBusinessRs envelope, code in `errorInformation`).
 *  - 16250-16298 are STORED outcomes: written on an import / page / crop /
 *    draft and shown from `CatalogStoredErrorDTO.message` — the server's text
 *    is already curator-safe, so the table below is only the fallback when a
 *    row carries a code without a message.
 *
 * No imports, so a bare node script can load it.
 */

export const CATALOG_IMPORT_ERROR = {
  FEATURE_DISABLED: "16201",
  UNAUTHENTICATED: "16202",
  ROLE_CHECK_FAILED: "16203",
  NOT_ALLOWED: "16204",
  BUSINESS_NOT_CLAIMED: "16205",
  CLAIM_CHECK_FAILED: "16206",
  VALIDATION_ERROR: "16207",
  FILE_TOO_LARGE: "16208",
  UNSUPPORTED_FILE_TYPE: "16209",
  TOO_MANY_PAGES: "16210",
  INVALID_PAGE_RANGE: "16211",
  DUPLICATE_IMPORT: "16212",
  IMPORT_ALREADY_RUNNING: "16213",
  DAILY_LIMIT_EXCEEDED: "16214",
  IMPORT_NOT_FOUND: "16215",
  NOT_IMPORT_OWNER: "16216",
  INVALID_IMPORT_STATE: "16217",
  UPLOAD_NOT_FOUND: "16218",
  UPLOAD_MISMATCH: "16219",
  PAGE_NOT_FOUND: "16220",
  DRAFT_NOT_FOUND: "16221",
  DRAFT_VERSION_CONFLICT: "16222",
  INVALID_DRAFT_STATE: "16223",
  INVALID_CROP_BOX: "16224",
  INVALID_MERGE: "16225",
  VARIANTS_NOT_SUPPORTED: "16226",
  SECTION_NOT_FOUND: "16227",
  NOTHING_TO_COMMIT: "16228",
  PROVIDER_UNAVAILABLE: "16229",
  STORAGE_UNAVAILABLE: "16230",
  RERUN_LIMIT_EXCEEDED: "16231",
  // stored outcomes
  UPLOAD_EXPIRED: "16250",
  PDF_UNREADABLE: "16251",
  PDF_ENCRYPTED: "16252",
  PDF_EMPTY: "16253",
  ALL_PAGES_FAILED: "16254",
  PAGE_RENDER_FAILED: "16255",
  PAGE_TOO_COMPLEX: "16256",
  PAGE_EXTRACTION_FAILED: "16257",
  PAGE_MODERATION_BLOCKED: "16258",
  PAGE_OUTPUT_INVALID: "16259",
  BATCH_EXPIRED: "16260",
  CROP_FAILED: "16261",
  VERIFY_SKIPPED: "16262",
  COMMIT_PRODUCT_INVALID: "16263",
  COMMIT_MEDIA_COPY_FAILED: "16264",
  COMMIT_FAILED: "16265",
  IMPORT_CANCELLED: "16266",
  CLEANED_UP: "16267",
  SOURCE_MISMATCH: "16268",
  PAGE_LIMIT_EXCEEDED: "16269",
  INTERNAL_ERROR: "16299",
} as const;

/** Raised by the client itself (no server answer). */
export const CATALOG_IMPORT_CLIENT_ERROR = {
  NETWORK: "CLIENT_NETWORK",
  ABORTED: "CLIENT_ABORTED",
  UPLOAD_REJECTED: "CLIENT_UPLOAD_REJECTED",
  UPLOAD_EXPIRED: "CLIENT_UPLOAD_EXPIRED",
} as const;

/** Messages copied from CatalogImportError (curator-safe by contract). */
export const CATALOG_IMPORT_ERROR_MESSAGES: Record<string, string> = {
  "16201": "PDF analysis is not available right now",
  "16202": "Please sign in again",
  "16203": "Could not verify your role. Please try again",
  "16204": "Only curators or admins can analyse PDFs",
  "16205": "You can only analyse PDFs for businesses you have claimed",
  "16206": "Could not verify your claim on this business. Please try again",
  "16207": "Some fields are missing or invalid",
  "16208": "This PDF is larger than the allowed size",
  "16209": "Only PDF files can be imported",
  "16210": "This PDF has more pages than one import allows. Choose a page range",
  "16211": "The page range is not valid for this PDF",
  "16212": "This PDF was already imported for this business",
  "16213": "Another PDF is being processed for this business. Try again when it is ready for review",
  "16214": "Daily page limit reached. Please try again tomorrow",
  "16215": "Import not found",
  "16216": "Only the curator who started this import or an admin can do this",
  "16217": "This action is not allowed at the import's current stage",
  "16218": "The PDF upload has not finished yet",
  "16219": "The uploaded file does not match the one selected. Please upload again",
  "16220": "Page not found in this import",
  "16221": "Product draft not found",
  "16222": "This product was changed in another tab. Reload to see the latest",
  "16223": "This product can't be changed now",
  "16224": "The selected area is too small or outside the page",
  "16225": "These products can't be combined",
  "16226": "This listing type does not support variants",
  "16227": "That section no longer exists for this business",
  "16228": "No approved products to create",
  "16229": "The AI service is unavailable right now. Please try again shortly",
  "16230": "File storage is unavailable right now. Please try again shortly",
  "16231": "Too many re-runs for this page today",
  "16250": "The PDF was never uploaded, so this import was closed",
  "16251": "This PDF could not be opened. It may be damaged",
  "16252": "This PDF is password-protected. Upload an unlocked copy",
  "16253": "This PDF has no pages",
  "16254": "No page of this PDF could be processed",
  "16255": "This page could not be read",
  "16256": "This page is too large to process automatically. Crop its products by hand",
  "16257": "The AI could not read this page",
  "16258": "This page was blocked by content safety checks",
  "16259": "The AI's answer for this page was unusable",
  "16260": "Processing took too long and was stopped. Retry the failed pages",
  "16261": "The product image could not be cut from the page",
  "16262": "Image checks were skipped; review images carefully",
  "16263": "The product could not be created",
  "16264": "The product's images could not be saved",
  "16265": "The product could not be created. Please retry",
  "16266": "This import was cancelled",
  "16267": "Pages and images of this import were removed after the retention period",
  "16268": "The stored PDF does not match the one selected. Please import again",
  "16269": "This PDF has more pages than one import allows. Import it again with a page range",
  "16299": "Something went wrong while processing the PDF analysis",
  CLIENT_NETWORK: "Couldn't reach the server. Check your connection and try again.",
  CLIENT_ABORTED: "Cancelled.",
  CLIENT_UPLOAD_REJECTED: "The upload was rejected. Please try again.",
  CLIENT_UPLOAD_EXPIRED: "The upload link expired. Please try again.",
};

/** Codes where trying the SAME request again can succeed (CatalogImportError#isRetryable). */
const RETRYABLE_CODES: ReadonlySet<string> = new Set([
  "16203",
  "16206",
  "16213",
  "16214",
  "16218",
  "16222",
  "16229",
  "16230",
  "16231",
  "16254",
  "16255",
  "16257",
  "16259",
  "16260",
  "16261",
  "16264",
  "16265",
  "16299",
  "CLIENT_NETWORK",
  "CLIENT_UPLOAD_EXPIRED",
]);

export const isRetryableCatalogImportCode = (code: string): boolean => RETRYABLE_CODES.has(code);

/** True for 16250-16298: reasons recorded on a row, never returned as a request error. */
export function isStoredOutcomeCode(code: string): boolean {
  const n = Number(code);
  return Number.isInteger(n) && n >= 16250 && n <= 16298;
}

/**
 * Curator-friendly text for an error code. Unknown codes (a newer server, or an
 * HTTP_5xx the gateway produced) use `fallback`.
 */
export function catalogImportErrorMessage(code: string | null | undefined, fallback: string): string {
  if (!code) return fallback;
  return CATALOG_IMPORT_ERROR_MESSAGES[code] ?? fallback;
}

/** Fallback when the server (or a gateway) gives no usable message at all. */
export const CATALOG_IMPORT_GENERIC_ERROR = "Something went wrong. Please try again.";

/** GET retry waits: 600 ms then 1.8 s (two retries), jittered +-20%. */
export function retryDelay(attempt: number, random: () => number = Math.random): number {
  const base = 600 * Math.pow(3, Math.max(0, attempt));
  return Math.round(base * (0.8 + random() * 0.4));
}
